/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable no-unused-vars,@typescript-eslint/no-unused-vars */
import React, { ReactElement } from 'react';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import Provider from '@com/provider';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { getQuery } from 'sutils';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import isEqual from 'lodash/isEqual';
import SlidePasteSpecialPanel from '@src/framework/frame-pc/components/paste-special-panel/slide-paste-panel';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import {
    SLIDE_RENDER_ENTRY,
} from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';

const loadAll = getQuery('loadAll') === '1';

// List显示方向
export enum Orientation {
    horizontal,
    vertical,
}

// 1.声明props类型
interface IListViewProps{
    loadSlideBuffer?: number;
    dataSource: Array<Array<IBaseModel>>;   // 数据源，是一个数组
    getCellID: (itemData) => string;
    getCellHeight?: (model: IBaseModel) => number;  // 纵向排版使用，获取单个cell的高度，可不传，将会自动获取每个Cell高度
    getCellWidth?: (index) => number; // 横向排版使用
    getCellView: (model: IBaseModel, editType: string) => ReactElement | null;
    orientation: Orientation,
    onScroll?: (e) => void;  // 监听滚动事件
    onCellAppearAtIndex?: (cell) => void;   // cell显示通知
    onCellDisappearAtIndex?: (cell) => void; // cell隐藏通知
    onVisibleCellIDsChange?: (visibleIds: Array<string>) => void; // 可视cell列表变化通知
    displayStateIds?: string[]; // 如果需要 ListView 来管理 model 的渲染状态，则将 model 的 id 放入其中
    style?: any;
    className?: string;
    editType: string;
    viewType: string;
    frameId?: string;
    cellClass: string;
    groupWrapperStyle?: React.CSSProperties;
    slideHeight: number;
    sectionWidth: number;
}

// 声明state类型
interface IListViewStates {
}

/**
 * ListView通用组件实现，用来显示列表。
 * 支持使用iframe渲染、支持横向纵向显示
 * 通过props.getCellView获取每个item的显示方式
 */
export default class ListView extends React.Component<IListViewProps, IListViewStates> {
    // 2.设定默认值，防止出错
    public static defaultProps = {
        dataSource: [],
        getSelectedArray: [],
    };

    // 通过渲染一次之后，获取的List宽高
    private listAutoHeight: number;
    private listAutoWidth: number;

    private listWrapper: any;
    private cellIDs: Array<string>;
    private visibleCellIDs: Array<string>;

    private handleSlideListScroll = throttle(() => {
        if (!this.shouldUpdate(this.props)) return;
        this.props.onScroll?.(this);
        this.handleCellDisplayState();
    }, 40);

    /**
     * resize处理
     */
    private handleResize = debounce(() => {
        if (!this.shouldUpdate(this.props)) return;
        this.handleCellDisplayState();
    }, 10, { maxWait: 100 });

    public constructor(props: IListViewProps) {
        super(props);

        this.state = {
        };

        this.listAutoWidth = 0;
        this.listAutoHeight = 0;

        this.cellIDs = [];
        this.visibleCellIDs = [];
    }

    public componentDidMount() {
        // resize
        window.addEventListener('resize', this.handleResize);
    }

    public shouldComponentUpdate(nextProps) {
        return this.shouldUpdate(nextProps);
    }

    public componentDidUpdate(prevProps): void {
        this.handleCellDisplayState();
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    public render() {
        const { style, className } = this.props;

        const usefulStyle = !browser.isSafari ? { willChange: 'transform', transform: 'translateZ(0)' } : null; // safari会导致一些情况不重新渲染，比如删除页面
        const listWrapperStyle: any = { ...usefulStyle, userSelect: 'none', WebkitUserSelect: 'none', width: '100%', ...style };
        if (browser.isFirefox) {
            listWrapperStyle.scrollbarWidth = 'thin';
        }
        return (<React.Fragment>
            <div
                ref={ref => (this.listWrapper = ref)}
                style={listWrapperStyle}
                className={`global-scroll ${className}`}
                onScroll={this.handleSlideListScroll.bind(this)}>
                { this.renderCells() }
            </div>
        </React.Fragment>);
    }

    /**
     * 如果纵向排列，没有传递cell的高度
     * 或者横向排列没有传递cell的宽度，就触发自动计算
     */
    private needSetAutoSize() {
        const vertical = this.props.orientation === Orientation.vertical; // 是否纵向显示
        if (vertical && this.props.getCellHeight) {
            return false;
        }
        return !(!vertical && this.props.getCellWidth);
    }

    /**
     * 将二维 dataSource 平铺成一维
     * @param {Array<Array<ISlideModel>>} dataSource
     * @returns ISlideModel[]
     */
    private getDataSourceTile(dataSource: Array<Array<IBaseModel>>): IBaseModel[] {
        const tileArr: IBaseModel[] = [];
        dataSource.forEach(group => group.forEach(item => tileArr.push(item)));
        return tileArr;
    }

    /**
     * 渲染列表
     */
    private renderCells(): Array<ReactElement> {
        const { dataSource, displayStateIds } = this.props;
        if (displayStateIds && displayStateIds.length !== 0) {
            this.cellIDs = displayStateIds;
        }
        return dataSource?.map((group, gIndex) => (<div
            style={{ ...this.props.groupWrapperStyle, position: 'relative' }}
            className="group-wrapper"
            key={gIndex}>
            {group.map((model) => {
                if ((!displayStateIds || displayStateIds.length === 0) && model.key) {
                    this.cellIDs.push(model.key);
                }
                return this.props.getCellView(model, this.props.editType);
            }).filter(e => !!e)}
            {this.renderPasteSpecial()}
        </div>));
    }

    private getContainer = () => this.listWrapper;

    private renderPasteSpecial = () => {
        const [visibleSectionAndSlideList] = this.props.dataSource;
        const editorAppService = AppServiceProvider.getInstance().getAppServices();
        // @ts-ignore
        const { currentSlide } = editorAppService.viewableServiceLayer.selectionService;

        let sectionNum = 0;
        let slideNum = 0;
        const targetSlideModel = visibleSectionAndSlideList.find((uiModel) => {
            // sectionModel
            if (uiModel.type === UIModelType.SECTION) {
                sectionNum += 1;
                return false;
            }
            // slideModel
            if (uiModel.key === currentSlide?.key) return true;
            slideNum += 1;
            return false;
        });

        if (!targetSlideModel) {
            sectionNum = 0;
            slideNum = 0;
        }

        return (<SlidePasteSpecialPanel
            slideHeight={this.props.slideHeight}
            sectionWidth={this.props.sectionWidth}
            sectionNum={sectionNum}
            slideNum={slideNum}
        />);
    };

    private handleCellDisplayState = () => {
        const visibleCellIDs = [];
        const listContainer: HTMLElement = this.getContainer();
        const scrollView = this.listWrapper;
        if (listContainer && scrollView) {
            // TODO: 这里假设 cells 为所有页面，但其实这个假设是错误的，比如有节收折的情况将不会渲染对应 cell dom
            // 另外，直接使用 dom api 获取节点在 react 中也是坏味道
            const cells: NodeListOf<Element> = listContainer.querySelectorAll(`.${this.props.cellClass}`);
            if (cells && cells.length > 0) {
                const listRect = scrollView.getBoundingClientRect();  // 这里要获取scrollView的rect
                const preCellShow = false;  // 上一个cell是否处于show状态
                this.handleDisplayForCells(
                    cells, listRect,
                    0, 0, preCellShow,
                    visibleCellIDs,
                );
            }
            const appService = (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices);
            appService.viewableServiceLayer.slideRenderStateManager
                .setListPanelSize(cells.length, SLIDE_RENDER_ENTRY.THUMBNAIL_PC);
        }
        if (!isEqual(this.visibleCellIDs, visibleCellIDs)) {
            this.visibleCellIDs = visibleCellIDs;
            this.props.onVisibleCellIDsChange?.(this.visibleCellIDs);
        }
    };

    private handleDisplayForCells(cells, listRect, leftOffset, topOffset, preCellShow, visibleCellIDs) {
        const deletedDisappearCells = new Set(this.visibleCellIDs);
        for (let i = 0; i < cells.length; i++) {
            const cellID = this.cellIDs[i];
            const cell = cells[i];
            const cellRect = cell.getBoundingClientRect();
            if (this.isOuterScreen(cellRect, leftOffset, listRect, topOffset)) {
                // 非可视区域
                // if (preCellShow) break;  // 优化一下，如果上一个显示，这个不显示，直接可以停止检查了
                // lasse 不能在此break，否则导致向上滚动时无法触发onCellDisappearAtIndex
                const indexInVisibleArray = this.visibleCellIDs.indexOf(cellID);
                if (indexInVisibleArray >= 0) {
                    this.props.onCellDisappearAtIndex?.(cell);
                }
            } else {
                // 可视区域
                // eslint-disable-next-line no-param-reassign
                preCellShow = true;
                visibleCellIDs.push(cellID);
                const indexInVisibleArray = this.visibleCellIDs.indexOf(cellID);
                if (indexInVisibleArray < 0) {
                    this.props.onCellAppearAtIndex?.(cell);
                }
            }
            deletedDisappearCells.delete(cellID);
        }
        deletedDisappearCells.forEach((cellID) => {
            this.props.onCellDisappearAtIndex?.({ id: cellID });
        });
    }

    private isOuterScreen(cellRect, leftOffset, listRect, topOffset) {
        if (loadAll) return false;
        const loadBuffer = this.props.loadSlideBuffer || 0;
        return cellRect.left - leftOffset >= listRect.right
            || cellRect.right - leftOffset <= listRect.left
            || cellRect.top - topOffset >= listRect.bottom + loadBuffer
            || cellRect.bottom - topOffset <= listRect.top - loadBuffer;
    }

    /**
     * 如果当前 editType 与 viewType 相同则响应更新
     * 避免 listView 隐藏后还在响应更新事件
     * @param props
     * @returns
     */
    private shouldUpdate(props) {
        return props.viewType === props.editType;
    }
}
